import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

const getLocalDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const splitToArray = (value, transform = (item) => item) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map(transform);

const normalizeCollection = (data, keys = []) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

const toIdString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value?._id || value?.id || value);
};

const getSubcategoryCollection = (category) => {
  if (!category || typeof category !== 'object') return [];
  if (Array.isArray(category.subCategories)) return category.subCategories;
  if (Array.isArray(category.subcategories)) return category.subcategories;
  if (Array.isArray(category.children)) return category.children;
  return [];
};

const getParentIdFromSubcategoryValue = (value) => {
  if (!value || typeof value !== 'object') return '';
  return toIdString(
    value.parentCategoryId ||
      value.parentCategory ||
      value.categoryId ||
      value.category ||
      value.parentId
  );
};

const emptyForm = {
  name: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  maxDiscountAmount: '',
  minProductPrice: '0',
  validFrom: getLocalDateTimeInput(new Date()),
  validUntil: '',
  isActive: true,
  isStackable: false,
  priority: '0',
  currencies: '',
  applyOn: 'all',
  categoryIds: [],
  subCategoryIds: []
};

const applyOnOptions = [
  { value: 'all', label: 'All products' },
  { value: 'products', label: 'Specific products' },
  { value: 'categories', label: 'Specific categories' },
  { value: 'subcategories', label: 'Specific subcategories' }
];

function SectionBlock({ title, subtitle, rightSlot, children }) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: '#fff',
        boxShadow: '0 10px 24px rgba(16, 24, 40, 0.06)'
      }}
    >
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {rightSlot ? <Box>{rightSlot}</Box> : null}
        </Box>
        {children}
      </Stack>
    </Box>
  );
}

export default function SaleForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState(emptyForm);
  const [productIdsText, setProductIdsText] = useState('');
  const [productFieldText, setProductFieldText] = useState('');
  const [productFieldTouched, setProductFieldTouched] = useState(false);
  const [subCategoryParentId, setSubCategoryParentId] = useState('');
  const [subCategoryFallbackNames, setSubCategoryFallbackNames] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const detailEndpoint = useMemo(() => (isEdit ? `/api/sales/admin/${id}` : null), [id, isEdit]);
  const { data: saleData, loading: loadingSale, error: loadError } = useGet(detailEndpoint);
  const { data: categoriesData } = useGet('/api/categories');
  const { data: productsData } = useGet('/api/products/');

  const categories = useMemo(
    () => normalizeCollection(categoriesData, ['categories', 'data', 'items']),
    [categoriesData]
  );
  const products = useMemo(
    () => normalizeCollection(productsData, ['products', 'data', 'items']),
    [productsData]
  );

  const subcategoryEndpoint = useMemo(
    () => (subCategoryParentId ? `/api/categories/subcategories/${subCategoryParentId}?all=true` : null),
    [subCategoryParentId]
  );
  const { data: subcategoriesData } = useGet(subcategoryEndpoint);
  const subcategories = useMemo(
    () => normalizeCollection(subcategoriesData, ['subCategories', 'subcategories', 'data', 'items']),
    [subcategoriesData]
  );

  const categoryNameById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      const idValue = toIdString(category);
      if (!idValue) return;
      map.set(idValue, category?.name || category?.categoryName || category?.title || idValue);
    });
    return map;
  }, [categories]);

  const subcategoryNameById = useMemo(() => {
    const map = new Map();
    Object.entries(subCategoryFallbackNames).forEach(([idValue, nameValue]) => {
      if (!idValue) return;
      map.set(idValue, nameValue || idValue);
    });
    categories.forEach((category) => {
      getSubcategoryCollection(category).forEach((subcategory) => {
        const idValue = toIdString(subcategory);
        if (!idValue) return;
        map.set(idValue, subcategory?.name || subcategory?.title || idValue);
      });
    });
    subcategories.forEach((subcategory) => {
      const idValue = toIdString(subcategory);
      if (!idValue) return;
      map.set(idValue, subcategory?.name || subcategory?.title || idValue);
    });
    return map;
  }, [categories, subcategories, subCategoryFallbackNames]);

  const productLabelById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const idValue = toIdString(product);
      if (!idValue) return;
      const sku = product?.sku || product?.SKU || '';
      if (sku) map.set(idValue, sku);
    });
    return map;
  }, [products]);

  const productIdBySku = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const idValue = toIdString(product);
      const sku = String(product?.sku || product?.SKU || '').trim();
      if (!idValue || !sku) return;
      map.set(sku.toLowerCase(), idValue);
    });
    return map;
  }, [products]);

  const handleProductInputChange = (event) => {
    const inputValue = event.target.value;
    setProductFieldText(inputValue);
    setProductFieldTouched(true);

    if (!isEdit) {
      setProductIdsText(inputValue);
      return;
    }

    const resolvedIds = splitToArray(inputValue, (item) => {
      const token = String(item || '').trim();
      if (!token) return '';
      return productIdBySku.get(token.toLowerCase()) || token;
    });
    setProductIdsText(resolvedIds.join(', '));
  };

  useEffect(() => {
    if (!isEdit || !saleData?.sale) return;
    const sale = saleData.sale;

    setFormData({
      name: sale.name || '',
      description: sale.description || '',
      discountType: sale.discountType || 'percentage',
      discountValue: String(sale.discountValue ?? ''),
      maxDiscountAmount: sale.maxDiscountAmount === null || sale.maxDiscountAmount === undefined ? '' : String(sale.maxDiscountAmount),
      minProductPrice: String(sale.minProductPrice ?? 0),
      validFrom: getLocalDateTimeInput(sale.validFrom),
      validUntil: getLocalDateTimeInput(sale.validUntil),
      isActive: Boolean(sale.isActive),
      isStackable: Boolean(sale.isStackable),
      priority: String(sale.priority ?? 0),
      currencies: Array.isArray(sale.currencies) ? sale.currencies.join(', ') : '',
      applyOn: sale.applyOn || 'all',
      categoryIds: Array.isArray(sale.categoryIds) ? sale.categoryIds.map((item) => toIdString(item)).filter(Boolean) : [],
      subCategoryIds: Array.isArray(sale.subCategoryIds) ? sale.subCategoryIds.map((item) => toIdString(item)).filter(Boolean) : []
    });

    const productIds = Array.isArray(sale.productIds) ? sale.productIds.map((item) => toIdString(item)).filter(Boolean) : [];
    setProductIdsText(productIds.join(', '));
    setProductFieldText(productIds.join(', '));
    setProductFieldTouched(false);

    let inferredParentId = '';
    if (Array.isArray(sale.categoryIds) && sale.categoryIds.length > 0) {
      inferredParentId = toIdString(sale.categoryIds[0]);
    }
    if (!inferredParentId && Array.isArray(sale.subCategoryIds) && sale.subCategoryIds.length > 0) {
      inferredParentId = getParentIdFromSubcategoryValue(sale.subCategoryIds[0]);
    }
    if (inferredParentId) {
      setSubCategoryParentId(inferredParentId);
    }
  }, [saleData, isEdit]);

  useEffect(() => {
    if (!isEdit || productFieldTouched) return;
    if (formData.applyOn !== 'products') return;
    if (!productFieldText) return;
    const withSku = splitToArray(productFieldText, (token) => productLabelById.get(token) || token).join(', ');
    if (withSku !== productFieldText) setProductFieldText(withSku);
  }, [formData.applyOn, isEdit, productFieldText, productFieldTouched, productLabelById]);

  useEffect(() => {
    if (formData.applyOn !== 'subcategories') return;
    if (subCategoryParentId) return;
    if (!formData.subCategoryIds.length || !categories.length) return;

    const selectedSet = new Set(formData.subCategoryIds);
    const inferredCategory = categories.find((category) =>
      getSubcategoryCollection(category).some((subcategory) => selectedSet.has(toIdString(subcategory)))
    );

    if (inferredCategory) {
      const inferredId = toIdString(inferredCategory);
      if (inferredId) setSubCategoryParentId(inferredId);
    }
  }, [categories, formData.applyOn, formData.subCategoryIds, subCategoryParentId]);

  useEffect(() => {
    if (formData.applyOn !== 'subcategories') return;
    if (subCategoryParentId) return;
    if (!formData.subCategoryIds.length || !categories.length) return;

    let isCancelled = false;
    const selectedIds = new Set(formData.subCategoryIds);

    const resolveParentAndNames = async () => {
      const fallbackNames = {};
      for (const category of categories) {
        const categoryId = toIdString(category);
        if (!categoryId) continue;

        try {
          const response = await apiClient.get(`/api/categories/subcategories/${categoryId}?all=true`);
          const list = normalizeCollection(response?.data, ['subCategories', 'subcategories', 'data', 'items']);

          list.forEach((subcategory) => {
            const idValue = toIdString(subcategory);
            if (!idValue || !selectedIds.has(idValue)) return;
            fallbackNames[idValue] = subcategory?.name || subcategory?.title || idValue;
          });

          const hasSelected = list.some((subcategory) => selectedIds.has(toIdString(subcategory)));
          if (hasSelected) {
            if (!isCancelled) {
              setSubCategoryParentId(categoryId);
              setSubCategoryFallbackNames((prev) => ({ ...prev, ...fallbackNames }));
            }
            return;
          }
        } catch (error) {
          // Ignore per-category fetch errors and continue.
        }
      }

      if (!isCancelled && Object.keys(fallbackNames).length) {
        setSubCategoryFallbackNames((prev) => ({ ...prev, ...fallbackNames }));
      }
    };

    resolveParentAndNames();
    return () => {
      isCancelled = true;
    };
  }, [categories, formData.applyOn, formData.subCategoryIds, subCategoryParentId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setErrorMessage('');
    setSuccessMessage('');
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'applyOn') {
        if (value !== 'categories') next.categoryIds = [];
        if (value !== 'subcategories') next.subCategoryIds = [];
      }

      return next;
    });
  };

  const handleMultiSelectChange = (name) => (event) => {
    const nextValue = event.target.value;
    setErrorMessage('');
    setSuccessMessage('');
    setFormData((prev) => ({
      ...prev,
      [name]: Array.isArray(nextValue) ? nextValue : []
    }));
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name,
      description: formData.description,
      discountType: formData.discountType,
      discountValue: Number(formData.discountValue),
      minProductPrice: Number(formData.minProductPrice || 0),
      validFrom: formData.validFrom,
      validUntil: formData.validUntil,
      isActive: formData.isActive,
      isStackable: formData.isStackable,
      priority: Number(formData.priority || 0),
      applyOn: formData.applyOn,
      currencies: splitToArray(formData.currencies, (item) => item.toUpperCase())
    };

    payload.maxDiscountAmount = formData.maxDiscountAmount === '' ? null : Number(formData.maxDiscountAmount);

    if (formData.applyOn === 'products') payload.productIds = splitToArray(productIdsText);
    if (formData.applyOn === 'categories') payload.categoryIds = formData.categoryIds;
    if (formData.applyOn === 'subcategories') payload.subCategoryIds = formData.subCategoryIds;

    return payload;
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Sale name is required.';
    if (!formData.validUntil) return 'Valid until date is required.';

    const discount = Number(formData.discountValue);
    if (!Number.isFinite(discount) || discount <= 0) return 'Discount value must be a positive number.';
    if (formData.discountType === 'percentage' && discount > 100) return 'Percentage discount cannot exceed 100.';

    const minProductPrice = Number(formData.minProductPrice);
    if (!Number.isFinite(minProductPrice) || minProductPrice < 0) return 'Minimum product price must be 0 or greater.';

    const priority = Number(formData.priority);
    if (!Number.isFinite(priority)) return 'Priority must be a valid number.';

    if (formData.applyOn === 'products' && splitToArray(productIdsText).length === 0) {
      return 'At least one product id is required for product-specific sale.';
    }
    if (formData.applyOn === 'categories' && formData.categoryIds.length === 0) return 'Select at least one category.';
    if (formData.applyOn === 'subcategories' && formData.subCategoryIds.length === 0) {
      return 'Select at least one subcategory.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const payload = buildPayload();
      if (isEdit) {
        await apiClient.patch(`/api/sales/admin/${id}`, payload);
        setSuccessMessage('Sale updated successfully.');
      } else {
        await apiClient.post('/api/sales/admin', payload);
        navigate('/sales');
        return;
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to save sale.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldProps = { fullWidth: true, size: 'small' };

  return (
    <Stack spacing={2.5}>
      <MainCard
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          p: 0
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2.5, md: 3 },
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            background: 'linear-gradient(120deg, #f1f8ff 0%, #fff 60%)'
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {isEdit ? 'Edit Sale' : 'Create Sale'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure product sale rules, stacking, and scope targeting.
              </Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={() => navigate('/sales')}>
              Back to list
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {loadError ? <Alert severity="error" sx={{ mb: 2 }}>Failed to load sale details.</Alert> : null}
          {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}
          {successMessage ? <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert> : null}

          {isEdit && loadingSale ? (
            <Typography>Loading sale...</Typography>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <SectionBlock title="Identity" subtitle="Primary sale identity and merchant note.">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField {...fieldProps} label="Sale Name" name="name" value={formData.name} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField {...fieldProps} type="number" label="Priority" name="priority" value={formData.priority} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField {...fieldProps} multiline minRows={3} label="Description" name="description" value={formData.description} onChange={handleChange} />
                    </Grid>
                  </Grid>
                </SectionBlock>

                <SectionBlock
                  title="Discount Setup"
                  subtitle="Configure value, caps, and product floor."
                  rightSlot={
                    <Chip
                      size="small"
                      color={formData.discountType === 'percentage' ? 'primary' : 'secondary'}
                      label={formData.discountType === 'percentage' ? 'Percentage' : 'Fixed'}
                    />
                  }
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField {...fieldProps} select label="Discount Type" name="discountType" value={formData.discountType} onChange={handleChange}>
                        <MenuItem value="percentage">Percentage</MenuItem>
                        <MenuItem value="fixed">Fixed amount</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...fieldProps}
                        type="number"
                        label="Discount Value"
                        name="discountValue"
                        value={formData.discountValue}
                        onChange={handleChange}
                        required
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{formData.discountType === 'percentage' ? '%' : 'Amt'}</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...fieldProps}
                        type="number"
                        label="Max Discount"
                        name="maxDiscountAmount"
                        value={formData.maxDiscountAmount}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField {...fieldProps} type="number" label="Min Product Price" name="minProductPrice" value={formData.minProductPrice} onChange={handleChange} />
                    </Grid>
                  </Grid>
                </SectionBlock>

                <SectionBlock
                  title="Targeting"
                  subtitle="Choose where this sale can be applied."
                  rightSlot={<Chip size="small" variant="outlined" label={applyOnOptions.find((item) => item.value === formData.applyOn)?.label || 'All products'} />}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField {...fieldProps} select label="Apply On" name="applyOn" value={formData.applyOn} onChange={handleChange}>
                        {applyOnOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {formData.applyOn === 'products' ? (
                      <Grid item xs={12} md={8}>
                        <TextField
                          {...fieldProps}
                          label={isEdit ? 'Product SKUs (comma separated)' : 'Product IDs (comma separated)'}
                          name="productIds"
                          value={productFieldText}
                          onChange={handleProductInputChange}
                          helperText={isEdit ? 'Editable as SKU. Payload still sends product IDs.' : undefined}
                        />
                      </Grid>
                    ) : null}

                    {formData.applyOn === 'categories' ? (
                      <Grid item xs={12} md={8}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="sale-categories-label">Categories</InputLabel>
                          <Select
                            labelId="sale-categories-label"
                            multiple
                            value={formData.categoryIds}
                            label="Categories"
                            onChange={handleMultiSelectChange('categoryIds')}
                            renderValue={(selected) => selected.map((idValue) => categoryNameById.get(idValue) || idValue).join(', ')}
                          >
                            {categories.map((category) => {
                              const idValue = toIdString(category);
                              return (
                                <MenuItem key={idValue} value={idValue}>
                                  {category?.name || category?.categoryName || category?.title || idValue}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : null}

                    {formData.applyOn === 'subcategories' ? (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            select
                            {...fieldProps}
                            label="Parent Category"
                            value={subCategoryParentId}
                            onChange={(event) => {
                              setSubCategoryParentId(event.target.value);
                              setFormData((prev) => ({ ...prev, subCategoryIds: [] }));
                            }}
                            helperText="Select category to load subcategories"
                          >
                            {categories.map((category) => {
                              const idValue = toIdString(category);
                              return (
                                <MenuItem key={idValue} value={idValue}>
                                  {category?.name || category?.categoryName || category?.title || idValue}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="sale-subcategories-label">Subcategories</InputLabel>
                            <Select
                              labelId="sale-subcategories-label"
                              multiple
                              value={formData.subCategoryIds}
                              label="Subcategories"
                              onChange={handleMultiSelectChange('subCategoryIds')}
                              renderValue={(selected) => selected.map((idValue) => subcategoryNameById.get(idValue) || idValue).join(', ')}
                            >
                              {subcategories.map((subcategory) => {
                                const idValue = toIdString(subcategory);
                                return (
                                  <MenuItem key={idValue} value={idValue}>
                                    {subcategory?.name || subcategory?.title || idValue}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </SectionBlock>

                <SectionBlock title="Rules and Window" subtitle="Activation, stacking, and availability period.">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          px: 1.5,
                          py: 1
                        }}
                      >
                        <Stack spacing={0.5}>
                          <FormControlLabel
                            control={<Checkbox checked={formData.isActive} name="isActive" onChange={handleChange} />}
                            label="Sale is active"
                          />
                          <FormControlLabel
                            control={<Checkbox checked={formData.isStackable} name="isStackable" onChange={handleChange} />}
                            label="Stackable with other sales"
                          />
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        {...fieldProps}
                        type="datetime-local"
                        label="Valid From"
                        name="validFrom"
                        InputLabelProps={{ shrink: true }}
                        value={formData.validFrom}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        {...fieldProps}
                        type="datetime-local"
                        label="Valid Until"
                        name="validUntil"
                        InputLabelProps={{ shrink: true }}
                        value={formData.validUntil}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        {...fieldProps}
                        label="Currencies (comma separated)"
                        name="currencies"
                        value={formData.currencies}
                        onChange={handleChange}
                        placeholder="USD, INR"
                      />
                    </Grid>
                  </Grid>
                </SectionBlock>

                <Box
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: '#fafafa',
                    px: { xs: 2, md: 3 },
                    py: 2
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ md: 'center' }}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip size="small" color={formData.isActive ? 'success' : 'default'} label={formData.isActive ? 'Active' : 'Inactive'} />
                        <Chip size="small" color={formData.isStackable ? 'info' : 'default'} label={formData.isStackable ? 'Stackable' : 'Not stackable'} />
                        <Chip size="small" variant="outlined" label={applyOnOptions.find((item) => item.value === formData.applyOn)?.label || 'All products'} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {formData.name || 'Sale name'}
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button variant="outlined" size="small" onClick={() => navigate('/sales')} disabled={submitting}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" size="small" disabled={submitting}>
                        {submitting ? 'Saving...' : isEdit ? 'Update Sale' : 'Create Sale'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </MainCard>
    </Stack>
  );
}
